/**
 * Raw response of `GET /clinics/{clinicSn}/timeseries/{operatingNumber}` — one
 * operating number's value across all of the clinic's QB years.
 *
 * `series` is a plain `{ [year]: value }` map on the wire; the
 * `entries/keys/values/isEmpty/size` shape in the OpenAPI document is only the
 * Spring `Map` serialization artefact and is not present in the JSON, so it is
 * deliberately not modelled here.
 */
export interface AysOperatingNumberTimeSeriesResponse {
  /** Localised metric labels keyed by locale (`de`, `en`). */
  label: Record<string, string>;
  /** Unit of the values (e.g. `Fälle`, `VK`, `Betten`). */
  unit: string;
  /** Per-year values keyed by the (string) year. */
  series: Record<string, number>;
}

/** One `(year, value)` point of a time series. */
export interface AysTimeSeriesPoint {
  year: number;
  value: number;
}

/**
 * A single operating-number time series, normalised for charting: the raw
 * year→value map is turned into year-ascending {@link AysTimeSeriesPoint}s and
 * the German label is resolved (falling back to English, then the metric id).
 */
export class AysOperatingNumberTimeSeries {
  public static fromObj(
    id: string,
    obj: AysOperatingNumberTimeSeriesResponse,
  ): AysOperatingNumberTimeSeries {
    const points = Object.entries(obj.series ?? {})
      .map(([year, value]) => ({ year: parseInt(year, 10), value }))
      .filter((point) => Number.isFinite(point.year))
      .sort((a, b) => a.year - b.year);

    return new AysOperatingNumberTimeSeries(
      id,
      obj.label?.['de'] ?? obj.label?.['en'] ?? id,
      obj.unit ?? '',
      points,
    );
  }

  constructor(
    public readonly id: string,
    public readonly label: string,
    public readonly unit: string,
    public readonly points: AysTimeSeriesPoint[],
  ) {}

  /** No usable data points (e.g. the clinic never reported this metric). */
  public get isEmpty(): boolean {
    return this.points.length === 0;
  }

  /** The most recent point, or `null` when {@link isEmpty}. */
  public get latest(): AysTimeSeriesPoint | null {
    return this.points.length ? this.points[this.points.length - 1] : null;
  }
}
